/**
 * Report Detail View Component - mirrors successful InterviewDetailView pattern
 */
import React, { useState } from 'react';
import { useToast } from '../common/Toast';
import reportService from '../../services/reportService';
import { formatDate } from '../../utils/formatters';

const ReportDetailView = ({ report, onBack, onUpdate }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [currentReport, setCurrentReport] = useState(report);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    report_name: report.report_name || '',
    report_description: report.report_description || '',
    person_name: report.person_name || '',
    job_description: report.job_description || '',
    company_name: report.company_name || '',
    key_findings: report.key_findings || ''
  });

  const handleScrape = async () => {
    try {
      setLoading(true);
      showToast('Scraping document...', 'info');
      
      const response = await reportService.scrapeDocument(currentReport.id);
      
      if (response && response.success) {
        showToast('✅ Document scraped successfully!', 'success');
        
        const updatedReport = {
          ...currentReport,
          content_extracted: true,
          content_text: response.data?.content_preview || '',
          status: 'processing'
        };
        setCurrentReport(updatedReport);
        
        if (onUpdate) {
          onUpdate(updatedReport);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Scraping failed';
        showToast(`❌ Scraping failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error scraping document:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Scraping error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateArticle = async () => {
    try {
      setLoading(true);
      showToast('Generating article from report...', 'info');
      
      const response = await reportService.generateArticle(currentReport.id);
      
      if (response && response.success) {
        showToast('✅ Article generated successfully!', 'success');
        
        const updatedReport = {
          ...currentReport,
          article_drafted: true
        };
        setCurrentReport(updatedReport);
        
        if (onUpdate) {
          onUpdate(updatedReport);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Article generation failed';
        showToast(`❌ Article generation failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error generating article:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Article generation error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      showToast('Deleting report...', 'info');
      
      const response = await reportService.deleteReport(currentReport.id);
      
      if (response && response.success) {
        showToast('✅ Report deleted successfully!', 'success');
        onBack();
      } else {
        const errorMsg = response?.error || response?.message || 'Delete failed';
        showToast(`❌ Delete failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Delete error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      showToast('Saving changes...', 'info');
      
      const response = await reportService.updateReport(currentReport.id, editData);
      
      if (response && response.success) {
        showToast('✅ Report updated successfully!', 'success');
        
        const updatedReport = { ...currentReport, ...editData };
        setCurrentReport(updatedReport);
        setIsEditing(false);
        
        if (onUpdate) {
          onUpdate(updatedReport);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Update failed';
        showToast(`❌ Update failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error updating report:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Update error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      report_name: currentReport.report_name || '',
      report_description: currentReport.report_description || '',
      person_name: currentReport.person_name || '',
      job_description: currentReport.job_description || '',
      company_name: currentReport.company_name || '',
      key_findings: currentReport.key_findings || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{currentReport.report_name}</h1>
              <p className="text-sm text-gray-600">{currentReport.file_name}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {!isEditing && (
              <>
                {!currentReport.content_extracted && (
                  <button
                    onClick={handleScrape}
                    disabled={loading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                        <span>Scraping...</span>
                      </>
                    ) : (
                      <span>Scrape Now</span>
                    )}
                  </button>
                )}
                
                {currentReport.content_extracted && (
                  <button
                    onClick={handleGenerateArticle}
                    disabled={loading}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <span>{currentReport.article_drafted ? 'Regenerate Article' : 'Generate Article'}</span>
                    )}
                  </button>
                )}
                
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Edit
                </button>
                
                <button
                  onClick={handleDelete}
                  className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors"
                >
                  Delete
                </button>
              </>
            )}
            
            {isEditing && (
              <>
                <button
                  onClick={handleSaveEdit}
                  disabled={loading}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                
                <button
                  onClick={handleCancelEdit}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
                
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Report Name</label>
                      <input
                        type="text"
                        value={editData.report_name}
                        onChange={(e) => setEditData({...editData, report_name: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={editData.report_description}
                        onChange={(e) => setEditData({...editData, report_description: e.target.value})}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Person Name</label>
                        <input
                          type="text"
                          value={editData.person_name}
                          onChange={(e) => setEditData({...editData, person_name: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                        <input
                          type="text"
                          value={editData.company_name}
                          onChange={(e) => setEditData({...editData, company_name: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Job Description</label>
                      <input
                        type="text"
                        value={editData.job_description}
                        onChange={(e) => setEditData({...editData, job_description: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Key Findings</label>
                      <textarea
                        value={editData.key_findings}
                        onChange={(e) => setEditData({...editData, key_findings: e.target.value})}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Report Name:</span>
                      <p className="text-gray-900 mt-1">{currentReport.report_name}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">File Name:</span>
                      <p className="text-gray-900 mt-1">{currentReport.file_name}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">File Type:</span>
                      <p className="text-gray-900 mt-1">{currentReport.file_type || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">File Size:</span>
                      <p className="text-gray-900 mt-1">
                        {currentReport.file_size ? `${(currentReport.file_size / 1024).toFixed(1)} KB` : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Uploaded:</span>
                      <p className="text-gray-900 mt-1">{formatDate(currentReport.created_at)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Status:</span>
                      <p className="text-gray-900 mt-1 capitalize">{currentReport.status}</p>
                    </div>
                    {currentReport.person_name && (
                      <div>
                        <span className="font-medium text-gray-700">Person Name:</span>
                        <p className="text-gray-900 mt-1">{currentReport.person_name}</p>
                      </div>
                    )}
                    {currentReport.company_name && (
                      <div>
                        <span className="font-medium text-gray-700">Company:</span>
                        <p className="text-gray-900 mt-1">{currentReport.company_name}</p>
                      </div>
                    )}
                    {currentReport.job_description && (
                      <div>
                        <span className="font-medium text-gray-700">Job Description:</span>
                        <p className="text-gray-900 mt-1">{currentReport.job_description}</p>
                      </div>
                    )}
                    {currentReport.source_url && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700">Source URL:</span>
                        <p className="text-gray-900 mt-1">
                          <a href={currentReport.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                            {currentReport.source_url}
                          </a>
                        </p>
                      </div>
                    )}
                    
                    {currentReport.report_description && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700 block mb-2">Description:</span>
                        <p className="text-gray-900 text-sm">{currentReport.report_description}</p>
                      </div>
                    )}
                    
                    {currentReport.key_findings && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700 block mb-2">Key Findings:</span>
                        <p className="text-gray-900 text-sm">{currentReport.key_findings}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Extracted Content */}
              {currentReport.content_extracted && currentReport.content_text && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Extracted Content</h2>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                      {currentReport.content_text}
                    </pre>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Content extracted • {currentReport.content_text.length} characters
                  </div>
                </div>
              )}

              {/* No Content State */}
              {!currentReport.content_extracted && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 mb-4 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-medium mb-2 text-gray-900">Content Not Extracted</h3>
                    <p className="text-gray-600 mb-4">Click "Scrape Now" to extract text content from this document.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              
              {/* Processing Status */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Content Extracted</span>
                    {currentReport.content_extracted ? (
                      <span className="text-green-600 text-lg">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg">✗</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Article Drafted</span>
                    {currentReport.article_drafted ? (
                      <span className="text-green-600 text-lg">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg">✗</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">WordPress Pushed</span>
                    {currentReport.wordpress_pushed ? (
                      <span className="text-green-600 text-lg">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg">✗</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDetailView;